// Small confirm / dismiss toggle group used inside finding cards. Clicking the
// active button clears it (back to null); clicking the inactive one switches.
// Visually: confirmed = green check, dismissed = red strikethrough icon.

import type { SkimFindingRecord } from '../../lib/storage';

export type EditorAction = NonNullable<SkimFindingRecord['editorAction']>;

interface Props {
  action: EditorAction | null | undefined;
  onChange: (next: EditorAction | null) => void;
  compact?: boolean;
}

export function FindingActionButtons({ action, onChange, compact }: Props) {
  const confirmed = action === 'confirmed';
  const dismissed = action === 'dismissed';
  const sizeClass = compact ? 'h-5 w-5 text-[10px]' : 'h-6 w-6 text-[11px]';
  return (
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        title={confirmed ? 'Confirmed — click to clear' : 'Confirm this finding'}
        aria-pressed={confirmed}
        onClick={() => onChange(confirmed ? null : 'confirmed')}
        className={`flex items-center justify-center rounded border ${sizeClass} ${
          confirmed
            ? 'border-emerald-500 bg-emerald-500 text-white'
            : 'border-slate-300 bg-white text-slate-400 hover:border-emerald-400 hover:text-emerald-600'
        }`}
      >
        ✓
      </button>
      <button
        type="button"
        title={dismissed ? 'Dismissed — click to clear' : 'Dismiss this finding'}
        aria-pressed={dismissed}
        onClick={() => onChange(dismissed ? null : 'dismissed')}
        className={`flex items-center justify-center rounded border ${sizeClass} ${
          dismissed
            ? 'border-red-500 bg-red-500 text-white'
            : 'border-slate-300 bg-white text-slate-400 hover:border-red-400 hover:text-red-600'
        }`}
      >
        ✗
      </button>
    </div>
  );
}

export function actionStyles(action: EditorAction | null | undefined): {
  container: string;
  text: string;
} {
  if (action === 'confirmed') {
    return { container: 'ring-1 ring-emerald-400', text: '' };
  }
  if (action === 'dismissed') {
    return { container: 'opacity-60', text: 'line-through' };
  }
  return { container: '', text: '' };
}
